import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronRight,
  Plus,
  Play,
  X,
} from "lucide-react";
import type { ButtonStyle, MessageMedia, Translation, TreeButton, TreePage } from "@/lib/api";
import { localizeTree, newKey, pruneTranslations, useLanguages } from "@/lib/i18n";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { languageName, tgHtmlToPlain, visibleLength, type BotLanguages } from "@kickwin/shared";
import { Button, Field, Input, Mono, Select } from "./primitives";
import { RichText, TgHtml } from "./rich-text";
import { LanguageTabs, TranslationNote } from "./language-tabs";

/**
 * The message editor (decision 35), shared by Auto reply, Commands and
 * Broadcasts.
 *
 * A message has text, an optional image or video, and rows of buttons. A
 * button either opens a link or opens a page — its own text, media and
 * buttons — so a whole help menu is one tree. The player taps through it
 * inside a single message, and every page below the first gets a Back
 * button. The operator edits one page at a time and walks the tree with a
 * breadcrumb, which works the same on a phone as on a desktop.
 */

export const CAPTION_LIMIT = 1024;
export const TEXT_LIMIT = 4096;
const MAX_DEPTH = 5;
const MAX_ROWS = 10;
const MAX_PER_ROW = 4;
const POST_LINK = /^https?:\/\/t\.me\/(c\/)?[A-Za-z0-9_]{1,64}\/[0-9]{1,12}\/?(\?.*)?$/;
const HTTP_LINK = /^https?:\/\/\S+\.\S+/;

type Hop = [row: number, col: number];

function pageAtPath(root: TreePage, path: Hop[]): TreePage {
  let page = root;
  for (const [r, c] of path) {
    const button = page.buttons[r]?.[c];
    if (!button || !("page" in button)) break;
    page = button.page;
  }
  return page;
}

/** Returns a copy of `root` with the page at `path` replaced by `fn(page)`. */
function updateAt(root: TreePage, path: Hop[], fn: (page: TreePage) => TreePage): TreePage {
  if (path.length === 0) return fn(root);
  const [[r, c], ...rest] = path as [Hop, ...Hop[]];
  return {
    ...root,
    buttons: root.buttons.map((row, ri) =>
      ri !== r
        ? row
        : row.map((button, ci) =>
            ci === c && "page" in button
              ? { ...button, page: updateAt(button.page, rest, fn) }
              : button,
          ),
    ),
  };
}

/** The first thing wrong anywhere in the tree, in words, or null. */
export function treeProblem(root: TreePage): string | null {
  const visit = (page: TreePage, depth: number, where: string): string | null => {
    if (!tgHtmlToPlain(page.text).trim()) return `${where} needs text.`;
    const limit = page.media ? CAPTION_LIMIT : TEXT_LIMIT;
    if (visibleLength(page.text) > limit)
      return `${where}: text ${page.media ? "under an image or video" : ""} can be at most ${limit} characters.`;
    if (page.media?.kind === "photo" && !HTTP_LINK.test(page.media.url))
      return `${where}: the image needs a full link starting with https://.`;
    if (page.media?.kind === "video" && !POST_LINK.test(page.media.post.trim()))
      return `${where}: paste a Telegram post link, like https://t.me/channel/123.`;
    for (const row of page.buttons) {
      for (const button of row) {
        if (!button.text.trim()) return `${where} has a button with no label.`;
        if ("url" in button && !/^(https?|tg):\/\/\S+/.test(button.url))
          return `“${button.text}” needs a full link starting with https://.`;
        if ("page" in button) {
          if (depth >= MAX_DEPTH) return `Menus can go at most ${MAX_DEPTH} levels deep.`;
          const problem = visit(button.page, depth + 1, `The “${button.text}” page`);
          if (problem) return problem;
        }
      }
    }
    return null;
  };
  const problem = visit(root, 0, "The message");
  if (problem) return problem;
  for (const [lang, t] of Object.entries(root.i18n ?? {})) {
    const translated = visit(localizeTree(root, t), 0, "The message");
    if (translated) return `${languageName(lang)}: ${translated}`;
  }
  return null;
}

export function MessageEditor({
  value,
  onChange,
  disabled,
  textPlaceholder,
  languages,
}: {
  value: TreePage;
  onChange: (page: TreePage) => void;
  disabled?: boolean;
  textPlaceholder?: string;
  /** The bot's languages; with more than one, the editor gets language tabs (decision 46). */
  languages?: BotLanguages | undefined;
}) {
  const { botId } = useParams();
  const botLanguages = useLanguages(botId);
  languages ??= botLanguages;
  const [path, setPath] = useState<Hop[]>([]);
  const [editing, setEditing] = useState<{ row: number; col: number | null } | null>(null);
  const [translatingButton, setTranslatingButton] = useState<Hop | null>(null);
  const [lang, setLang] = useState<string | null>(null);

  const page = pageAtPath(value, path);
  const setPage = (fn: (p: TreePage) => TreePage) => onChange(updateAt(value, path, fn));

  // --- translation mode (decision 46) ---
  const translation: Translation = (lang && value.i18n?.[lang]) || {};
  const setTranslation = (fn: (t: Translation) => Translation) => {
    if (!lang) return;
    onChange({ ...value, i18n: { ...value.i18n, [lang]: fn(translation) } });
  };
  const setNode = (k: string, patch: Record<string, string | undefined>) =>
    setTranslation((t) => ({
      ...t,
      nodes: { ...t.nodes, [k]: { ...t.nodes?.[k], ...patch } },
    }));
  const shown = lang ? pageAtPath(localizeTree(value, translation), path) : page;
  // The key of the button whose page is open; its page's words are stored under it.
  const pageKey = (() => {
    if (path.length === 0) return null;
    const [r, c] = path.at(-1)!;
    return pageAtPath(value, path.slice(0, -1)).buttons[r]?.[c]?.k ?? null;
  })();
  const translatedText = lang
    ? ((pageKey ? translation.nodes?.[pageKey]?.text : translation.text) ?? "")
    : "";
  const setTranslatedText = (text: string) => {
    if (pageKey) setNode(pageKey, { text });
    else setTranslation((t) => ({ ...t, text }));
  };

  // Names for the breadcrumb: the label of each button walked through.
  const crumbs: string[] = [];
  {
    let p = lang ? localizeTree(value, translation) : value;
    for (const [r, c] of path) {
      const b = p.buttons[r]?.[c];
      if (!b || !("page" in b)) break;
      crumbs.push(b.text || "Untitled");
      p = b.page;
    }
  }

  const moveRow = (r: number, by: -1 | 1) =>
    setPage((p) => {
      const rows = [...p.buttons];
      const [row] = rows.splice(r, 1);
      rows.splice(r + by, 0, row!);
      return { ...p, buttons: rows };
    });

  const moveButton = (r: number, c: number, by: -1 | 1) =>
    setPage((p) => ({
      ...p,
      buttons: p.buttons.map((row, ri) => {
        if (ri !== r) return row;
        const next = [...row];
        const [b] = next.splice(c, 1);
        next.splice(c + by, 0, b!);
        return next;
      }),
    }));

  const removeButton = (r: number, c: number) =>
    setPage((p) => ({
      ...p,
      buttons: p.buttons
        .map((row, ri) => (ri === r ? row.filter((_, ci) => ci !== c) : row))
        .filter((row) => row.length > 0),
    }));

  const saveButton = (button: TreeButton) => {
    if (!editing) return;
    const { row, col } = editing;
    setPage((p) => {
      if (row >= p.buttons.length) return { ...p, buttons: [...p.buttons, [button]] };
      return {
        ...p,
        buttons: p.buttons.map((rw, ri) =>
          ri !== row
            ? rw
            : col === null
              ? [...rw, button]
              : rw.map((b, ci) => (ci === col ? button : b)),
        ),
      };
    });
    setEditing(null);
  };

  const limit = page.media ? CAPTION_LIMIT : TEXT_LIMIT;
  const length = visibleLength(shown.text);
  const editingButton =
    editing && editing.col !== null ? (page.buttons[editing.row]?.[editing.col] ?? null) : null;
  const translatingTarget = translatingButton
    ? (page.buttons[translatingButton[0]]?.[translatingButton[1]] ?? null)
    : null;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_300px] xl:items-start">
      <fieldset disabled={disabled} className="grid min-w-0 gap-3">
        <LanguageTabs
          languages={languages}
          value={lang}
          onChange={(next) => {
            setLang(next);
            setEditing(null);
            setTranslatingButton(null);
          }}
          translated={(l) => !!pruneTranslations(value.i18n)?.[l]}
        />
        {lang && <TranslationNote lang={lang} />}

        {path.length > 0 && (
          <nav
            aria-label="Where you are"
            className="flex flex-wrap items-center gap-1 text-muted-foreground"
          >
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => setPath([])}
            >
              Message
            </button>
            {crumbs.map((name, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="size-3.5" />
                {i === crumbs.length - 1 ? (
                  <span className="font-medium text-foreground">{name}</span>
                ) : (
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={() => setPath(path.slice(0, i + 1))}
                  >
                    {name}
                  </button>
                )}
              </span>
            ))}
          </nav>
        )}

        <Field group label={path.length === 0 ? "Message" : "Page text"}>
          <RichText
            // One editor per page and language, so each starts fresh.
            key={`${lang ?? ""}:${path.map((h) => h.join(".")).join("/")}`}
            value={lang ? translatedText : page.text}
            onChange={(text) => (lang ? setTranslatedText(text) : setPage((p) => ({ ...p, text })))}
            placeholder={
              lang
                ? clip(tgHtmlToPlain(page.text)) || "Translation"
                : path.length === 0
                  ? textPlaceholder
                  : "What this page says"
            }
            label={path.length === 0 ? "Message" : "Page text"}
          />
        </Field>
        <div
          className={cn(
            "-mt-1.5 text-right",
            length > limit ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {lang && !tgHtmlToPlain(translatedText).trim() && (
            <span className="mr-2">Uses the default text.</span>
          )}
          <Mono>{length}</Mono> / {limit}
          {page.media && " (text under an image or video)"}
        </div>

        {!lang && (
          <MediaField media={page.media} onChange={(media) => setPage((p) => ({ ...p, media }))} />
        )}

        <div className="grid gap-2">
          <div className="font-medium">Buttons</div>
          {lang && page.buttons.length === 0 && (
            <p className="text-muted-foreground">No buttons on this page.</p>
          )}
          {shown.buttons.map((row, r) => (
            <div key={r} className="flex items-stretch gap-1.5">
              <div className="grid min-w-0 flex-1 gap-1.5 sm:grid-flow-col sm:auto-cols-fr">
                {row.map((button, c) => (
                  <ButtonChip
                    key={c}
                    button={button}
                    locked={!!lang}
                    onEdit={() =>
                      lang ? setTranslatingButton([r, c]) : setEditing({ row: r, col: c })
                    }
                    onOpen={"page" in button ? () => setPath([...path, [r, c]]) : undefined}
                    onLeft={c > 0 ? () => moveButton(r, c, -1) : undefined}
                    onRight={c < row.length - 1 ? () => moveButton(r, c, 1) : undefined}
                    onRemove={() => removeButton(r, c)}
                  />
                ))}
              </div>
              {!lang && (
                <div className="flex shrink-0 flex-col justify-center gap-0.5">
                  <Icon label="Move row up" disabled={r === 0} onClick={() => moveRow(r, -1)}>
                    <ArrowUp />
                  </Icon>
                  {row.length < MAX_PER_ROW && (
                    <Icon
                      label="Add a button to this row"
                      onClick={() => setEditing({ row: r, col: null })}
                    >
                      <Plus />
                    </Icon>
                  )}
                  <Icon
                    label="Move row down"
                    disabled={r === page.buttons.length - 1}
                    onClick={() => moveRow(r, 1)}
                  >
                    <ArrowDown />
                  </Icon>
                </div>
              )}
            </div>
          ))}
          {!lang && page.buttons.length < MAX_ROWS && (
            <button
              type="button"
              onClick={() => setEditing({ row: page.buttons.length, col: null })}
              className="flex h-10 items-center justify-center gap-1.5 rounded-md border border-dashed border-border text-muted-foreground hover:border-ring hover:text-foreground"
            >
              <Plus className="size-4" /> Add a row of buttons
            </button>
          )}
        </div>

        {path.length > 0 && (
          <Field label="Back button">
            {lang && pageKey ? (
              <Input
                value={translation.nodes?.[pageKey]?.back ?? ""}
                onChange={(e) => setNode(pageKey, { back: e.target.value || undefined })}
                placeholder={page.backLabel ?? "↩️ Back"}
                maxLength={64}
                className="md:max-w-xs"
              />
            ) : (
              <Input
                value={page.backLabel ?? ""}
                onChange={(e) => setPage((p) => ({ ...p, backLabel: e.target.value || undefined }))}
                placeholder="↩️ Back"
                maxLength={64}
                className="md:max-w-xs"
              />
            )}
          </Field>
        )}
      </fieldset>

      <Preview page={shown} showBack={path.length > 0} />

      <ButtonDialog
        key={
          editing
            ? `${path.map((h) => h.join(".")).join("/")}:${editing.row}:${editing.col}`
            : "closed"
        }
        open={editing !== null}
        initial={editingButton}
        allowPage={path.length < MAX_DEPTH}
        onCancel={() => setEditing(null)}
        onSave={saveButton}
      />

      {lang && translatingTarget?.k && (
        <TranslateButtonDialog
          key={`${lang}:${translatingTarget.k}`}
          button={translatingTarget}
          words={translation.nodes?.[translatingTarget.k] ?? {}}
          onCancel={() => setTranslatingButton(null)}
          onSave={(words) => {
            setNode(translatingTarget.k!, words);
            setTranslatingButton(null);
          }}
        />
      )}
    </div>
  );
}

/** A short hint of the default text, for a translation's placeholder. */
function clip(text: string): string {
  const one = text.replace(/\s+/g, " ").trim();
  return one.length > 90 ? `${one.slice(0, 90)}…` : one;
}

/** A button's words in one language: its label, and its reply if it asks our team. */
function TranslateButtonDialog({
  button,
  words,
  onCancel,
  onSave,
}: {
  button: TreeButton;
  words: { label?: string | undefined; reply?: string | undefined };
  onCancel: () => void;
  onSave: (words: { label: string | undefined; reply?: string | undefined }) => void;
}) {
  const [label, setLabel] = useState(words.label ?? "");
  const [reply, setReply] = useState(words.reply ?? "");
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/45" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-50 grid max-h-[90dvh] w-[min(440px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-xl"
        >
          <Dialog.Title className="font-semibold">Translate “{button.text}”</Dialog.Title>
          <Field label="Label">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={button.text}
              maxLength={64}
              autoFocus
            />
          </Field>
          {"help" in button && (
            <Field group label="Reply to the player">
              <RichText
                compact
                value={reply}
                onChange={setReply}
                placeholder={clip(tgHtmlToPlain(button.help.reply ?? "")) || DEFAULT_HELP_HINT}
                label="Reply to the player"
              />
            </Field>
          )}
          <p className="text-muted-foreground">Leave a field empty to use the default.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() =>
                onSave({
                  label: label.trim() || undefined,
                  ...("help" in button ? { reply: reply || undefined } : {}),
                })
              }
            >
              Save translation
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const DEFAULT_HELP_HINT = "✅ Thanks — our team has been notified and will reply here shortly.";

function MediaField({
  media,
  onChange,
}: {
  media: MessageMedia | undefined;
  onChange: (media: MessageMedia | undefined) => void;
}) {
  const kind = media?.kind ?? "none";
  return (
    <div className="grid gap-2 sm:grid-cols-[200px_minmax(0,1fr)] sm:items-end">
      <Field label="Image or video">
        <Select
          className="w-full"
          value={kind}
          onChange={(e) => {
            const next = e.target.value;
            onChange(
              next === "photo"
                ? { kind: "photo", url: "" }
                : next === "video"
                  ? { kind: "video", post: "" }
                  : undefined,
            );
          }}
        >
          <option value="none">None</option>
          <option value="photo">Image (link)</option>
          <option value="video">Video (Telegram post)</option>
        </Select>
      </Field>
      {media?.kind === "photo" && (
        <Input
          aria-label="Image link"
          className="mono"
          value={media.url}
          onChange={(e) => onChange({ kind: "photo", url: e.target.value.trim() })}
          placeholder="https://kickwin.site/promo.jpg"
        />
      )}
      {media?.kind === "video" && (
        <Input
          aria-label="Telegram post link"
          className="mono"
          value={media.post}
          onChange={(e) => onChange({ kind: "video", post: e.target.value.trim() })}
          placeholder="https://t.me/kickwinofficial/564"
        />
      )}
      {media?.kind === "photo" && (
        <p className="text-muted-foreground sm:col-span-2">
          A JPG or PNG up to 5 MB that anyone can open.
        </p>
      )}
      {media?.kind === "video" && (
        <p className="text-muted-foreground sm:col-span-2">
          Copied from the channel post. The bot must be able to read that channel — add it to the
          channel if it is private.
        </p>
      )}
    </div>
  );
}

const STYLE_CLASS: Record<ButtonStyle | "default", string> = {
  default: "bg-secondary text-foreground",
  danger: "bg-red-500 text-white",
  primary: "bg-blue-500 text-white",
  success: "bg-green-600 text-white",
};

function ButtonChip({
  button,
  onEdit,
  onOpen,
  onLeft,
  onRight,
  onRemove,
  locked,
}: {
  button: TreeButton;
  onEdit: () => void;
  onOpen?: (() => void) | undefined;
  onLeft?: (() => void) | undefined;
  onRight?: (() => void) | undefined;
  onRemove: () => void;
  /** Translating: the structure cannot change, only the words. */
  locked?: boolean;
}) {
  return (
    <div className="grid min-w-0 gap-1 rounded-md border border-border bg-card p-1.5">
      <button
        type="button"
        onClick={onEdit}
        className={cn(
          "min-h-9 truncate rounded px-2 py-1.5 text-center font-medium",
          STYLE_CLASS[button.style ?? "default"],
        )}
        title={locked ? "Translate this button" : "Edit this button"}
      >
        {button.text || "Untitled"}
      </button>
      <div className="flex items-center gap-0.5">
        {"help" in button ? (
          <span className="mr-auto truncate px-1 text-muted-foreground">🙋 Asks our team</span>
        ) : onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            className="mr-auto flex min-w-0 items-center gap-0.5 truncate px-1 text-primary hover:underline"
          >
            Open page <ChevronRight className="size-3.5 shrink-0" />
          </button>
        ) : (
          <span
            className="mr-auto truncate px-1 text-muted-foreground"
            title={"url" in button ? button.url : ""}
          >
            ↗ Link
          </span>
        )}
        {!locked && (
          <>
            <Icon label="Move left" disabled={!onLeft} onClick={() => onLeft?.()}>
              <ArrowLeft />
            </Icon>
            <Icon label="Move right" disabled={!onRight} onClick={() => onRight?.()}>
              <ArrowRight />
            </Icon>
            <Icon label="Remove button" onClick={onRemove}>
              <X />
            </Icon>
          </>
        )}
      </div>
    </div>
  );
}

function ButtonDialog({
  open,
  initial,
  allowPage,
  onCancel,
  onSave,
}: {
  open: boolean;
  initial: TreeButton | null;
  allowPage: boolean;
  onCancel: () => void;
  onSave: (button: TreeButton) => void;
}) {
  const [text, setText] = useState(initial?.text ?? "");
  const [style, setStyle] = useState<ButtonStyle | undefined>(initial?.style);
  const [action, setAction] = useState<"page" | "url" | "help">(
    initial
      ? "url" in initial
        ? "url"
        : "help" in initial
          ? "help"
          : "page"
      : allowPage
        ? "page"
        : "url",
  );
  const [url, setUrl] = useState(initial && "url" in initial ? initial.url : "");
  const [reply, setReply] = useState(
    initial && "help" in initial ? (initial.help.reply ?? "") : "",
  );
  const [touched, setTouched] = useState(false);

  const problem = !text.trim()
    ? "Give the button a label."
    : action === "url" && !/^(https?|tg):\/\/\S+/.test(url.trim())
      ? "Enter a full link starting with https://."
      : null;

  const save = () => {
    setTouched(true);
    if (problem) return;
    // The id stays with the button, so its translations do too (decision 46).
    const base = { text: text.trim(), k: initial?.k ?? newKey(), ...(style ? { style } : {}) };
    if (action === "url") onSave({ ...base, url: url.trim() });
    else if (action === "help")
      onSave({ ...base, help: tgHtmlToPlain(reply).trim() ? { reply } : {} });
    else
      onSave({
        ...base,
        // Keep the page the button already had; a new one starts empty.
        page: initial && "page" in initial ? initial.page : { text: "", buttons: [] },
      });
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/45" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed top-1/2 left-1/2 z-50 grid max-h-[90dvh] w-[min(440px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-xl"
        >
          <Dialog.Title className="font-semibold">
            {initial ? "Edit button" : "New button"}
          </Dialog.Title>
          <Field label="Label">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="🏦 Deposit Support"
              maxLength={64}
              autoFocus
            />
          </Field>
          <div className="grid gap-1.5">
            <span className="font-medium">Colour</span>
            <div className="flex gap-2">
              {(["default", "danger", "primary", "success"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-label={
                    s === "default"
                      ? "Default"
                      : s === "danger"
                        ? "Red"
                        : s === "primary"
                          ? "Blue"
                          : "Green"
                  }
                  aria-pressed={(style ?? "default") === s}
                  onClick={() => setStyle(s === "default" ? undefined : s)}
                  className={cn(
                    "h-9 flex-1 rounded-md border-2",
                    (style ?? "default") === s ? "border-ring" : "border-transparent",
                  )}
                >
                  <span className={cn("block h-full w-full rounded", STYLE_CLASS[s])} />
                </button>
              ))}
            </div>
          </div>
          <fieldset className="grid gap-2">
            <legend className="mb-1.5 font-medium">When tapped</legend>
            {allowPage && (
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="action"
                  checked={action === "page"}
                  onChange={() => setAction("page")}
                  className="mt-1"
                />
                <span>
                  Opens a page
                  <span className="block text-muted-foreground">
                    The message changes to this page's text and buttons, with Back.
                  </span>
                </span>
              </label>
            )}
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="action"
                checked={action === "url"}
                onChange={() => setAction("url")}
                className="mt-1"
              />
              <span>Opens a link</span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="radio"
                name="action"
                checked={action === "help"}
                onChange={() => setAction("help")}
                className="mt-1"
              />
              <span>
                Asks our team
                <span className="block text-muted-foreground">
                  Opens the player's Topic in the CS group and posts which button they tapped, so an
                  agent can pick it up.
                </span>
              </span>
            </label>
            {action === "help" && (
              <Field group label="Reply to the player (optional)">
                <RichText
                  compact
                  value={reply}
                  onChange={setReply}
                  placeholder="✅ Thanks — our team has been notified and will reply here shortly."
                  label="Reply to the player"
                />
              </Field>
            )}
            {action === "url" && (
              <Input
                className="mono"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://kickwin.site/promotions"
              />
            )}
            {action === "page" && initial && "url" in initial && (
              <p className="text-muted-foreground">
                After saving, tap “Open page” on the button to write the page.
              </p>
            )}
          </fieldset>
          {touched && problem && <p className="text-destructive">{problem}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="quiet" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" onClick={save}>
              {initial ? "Save button" : "Add button"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Roughly what the player sees on this page. */
function Preview({ page, showBack }: { page: TreePage; showBack: boolean }) {
  return (
    <div className="rounded-lg border border-border">
      <div className="border-b border-border px-3 py-2 font-medium">Preview</div>
      <div className="grid gap-1 bg-[oklch(0.93_0.02_220)] p-3 dark:bg-[oklch(0.25_0.02_240)]">
        <div className="max-w-[92%] overflow-hidden rounded-lg rounded-bl-sm bg-card shadow-sm">
          {page.media?.kind === "photo" && page.media.url && (
            <img src={page.media.url} alt="" className="max-h-56 w-full object-cover" />
          )}
          {page.media?.kind === "video" && (
            <div className="grid h-36 place-items-center bg-neutral-800 text-white/80">
              <div className="grid justify-items-center gap-1">
                <Play className="size-8" />
                <span className="mono max-w-[240px] truncate px-2 text-[11px]">
                  {page.media.post || "video"}
                </span>
              </div>
            </div>
          )}
          <div className="px-3 py-2">
            {tgHtmlToPlain(page.text).trim() ? (
              <TgHtml html={page.text} />
            ) : (
              <span className="text-muted-foreground">Message text</span>
            )}
          </div>
        </div>
        {(page.buttons.length > 0 || showBack) && (
          <div className="grid max-w-[92%] gap-1">
            {page.buttons.map((row, r) => (
              <div key={r} className="flex gap-1">
                {row.map((b, i) => (
                  <div
                    key={i}
                    className={cn(
                      "min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-center shadow-sm",
                      b.style ? STYLE_CLASS[b.style] : "bg-card/80",
                    )}
                  >
                    {b.text || "…"}
                    {"url" in b && <span className="ml-1 opacity-70">↗</span>}
                  </div>
                ))}
              </div>
            ))}
            {showBack && (
              <div className="rounded-md bg-card/80 px-2 py-1.5 text-center shadow-sm">
                {page.backLabel || "↩️ Back"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Icon({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-7 place-items-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-25 [&_svg]:size-3.5 [@media(pointer:coarse)]:size-9"
    >
      {children}
    </button>
  );
}
